import { DayLabel, TableActions, TableContainer } from '@shared/components'
import { useLocales } from '@shared/locales'
import { Flex, TableColumnsType, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import { Edit2, Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'

import { AddEditOffer } from './AddEditOffer'
import { DeleteOffer } from './DeleteOffer'
import { mockOffers } from './mockData'
import { OffersHeader } from './OffersHeader'
import { PreviewOffer } from './PreviewOffer'
import { ActionsType, Offer } from './types'

type ModalType = 'add' | 'edit' | 'delete' | 'preview'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  add: AddEditOffer,
  edit: AddEditOffer,
  delete: DeleteOffer,
  preview: PreviewOffer
}

const statusColors: Record<Offer['status'], string> = {
  pending: 'orange',
  approved: 'green',
  rejected: 'red',
  active: 'blue',
  expired: 'default'
}

const formatPrice = (price: number, currency: string) => {
  return `${price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`
}

export const OffersCol = () => {
  const { t } = useLocales()
  const [offers, setOffers] = useState<Offer[]>(mockOffers)
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    offer: Offer | null
  }>({ offer: null, type: null })

  const actionItems = (offer: Offer): MenuProps['items'] => [
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ offer, type: 'edit' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ offer, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<Offer> = [
    {
      title: t('offers.offer_number'),
      dataIndex: 'offer_number',
      key: 'offer_number'
    },
    {
      title: t('offers.location'),
      dataIndex: 'location',
      key: 'location'
    },
    {
      title: t('offers.media_type_label'),
      dataIndex: 'media_type',
      key: 'media_type',
      render: (media_type: Offer['media_type']) =>
        t(`offers.media_type.${media_type}`)
    },
    {
      title: t('offers.format'),
      dataIndex: 'format',
      key: 'format'
    },
    {
      title: t('offers.proportions'),
      dataIndex: 'proportions',
      key: 'proportions'
    },
    {
      title: t('offers.resolution'),
      dataIndex: 'resolution',
      key: 'resolution'
    },
    {
      title: t('offers.frames_per_second'),
      dataIndex: 'frames_per_second',
      key: 'frames_per_second',
      render: (frames_per_second?: number) =>
        frames_per_second ? `${frames_per_second} ${t('offers.fps')}` : '-'
    },
    {
      title: t('offers.duration'),
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      render: (duration_seconds?: number) =>
        duration_seconds ? `${duration_seconds} ${t('offers.seconds')}` : '-'
    },
    {
      title: t('offers.font_size_minimum'),
      dataIndex: 'font_size_minimum',
      key: 'font_size_minimum',
      render: (font_size_minimum) => `${font_size_minimum}`
    },
    {
      title: t('offers.maximum_contrast'),
      dataIndex: 'maximum_contrast',
      key: 'maximum_contrast',
      render: (maximum_contrast: boolean) =>
        maximum_contrast ? t('buttons.yes') : t('buttons.no')
    },
    {
      title: t('offers.price'),
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => formatPrice(price, record.currency)
    },
    {
      title: t('lead.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: Offer['status']) => (
        <Tag color={statusColors[status]}>{t(`offers.status.${status}`)}</Tag>
      )
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => <DayLabel date={created_at} />
    },
    {
      title: t('offers.period'),
      key: 'period',
      render: (_, record) => (
        <span>
          {new Date(record.start_date).toLocaleDateString()} -{' '}
          {new Date(record.end_date).toLocaleDateString()}
        </span>
      )
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, offer) => <TableActions options={actionItems(offer)} />
    }
  ]

  const actionComponent = useMemo(() => {
    if (!modalState.type) return null

    const Component = modalTypes[modalState.type]

    const handleSuccess = (updatedOffers?: Offer[]) => {
      if (updatedOffers) {
        // If generating a new offer (add), replace the existing one (only one allowed)
        if (modalState.type === 'add') {
          setOffers([updatedOffers[0]])
        } else {
          setOffers(updatedOffers)
        }
      }
      setModalState({ type: null, offer: null })
    }

    return (
      <Component
        open
        offer={modalState.offer ?? undefined}
        onCancel={() => setModalState({ type: null, offer: null })}
        onSuccess={handleSuccess}
      />
    )
  }, [modalState.type, modalState.offer])

  const handleRowClick = (record: Offer) => {
    setModalState({ offer: record, type: 'preview' })
  }

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        dataSource={offers.slice(0, 1)}
        loading={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        rightChildren={
          <OffersHeader
            onAdd={() => setModalState({ type: 'add', offer: null })}
          />
        }
      />

      {actionComponent}
    </Flex>
  )
}
